import React, { useState, useEffect } from 'react';
import { WizardLayout } from './components/WizardLayout';
import { Step1 } from './components/wizard/Step1';
import { Step2 } from './components/wizard/Step2';
import { Step3 } from './components/wizard/Step3';
import { Step4 } from './components/wizard/Step4';
import { Step5 } from './components/wizard/Step5';
import { SuccessView } from './components/wizard/SuccessView';
import { submitServiceRequest } from './lib/api/submitClient';
import { buildPayload, WizardState } from './utils/payloadBuilder';
import { colors } from './theme/colors';
import { spacing } from './theme/spacing';

const STEPS = ['Vene & moottori', 'Huolto', 'Ajankohta & toimitus', 'Yhteystiedot', 'Yhteenveto'];

export const BookingFlow: React.FC = () => {
    // Initialize step from URL
    const getInitialStep = () => {
        const params = new URLSearchParams(window.location.search);
        const s = parseInt(params.get('step') || '1', 10);
        return (s >= 1 && s <= 5) ? s : 1;
    };

    const [step, setStep] = useState(getInitialStep());
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Sync URL when step changes
    useEffect(() => {
        const url = new URL(window.location.href);
        url.searchParams.set('step', step.toString());
        window.history.pushState({}, '', url.toString());
        window.scrollTo(0, 0);
    }, [step]);

    // Handle back button
    useEffect(() => {
        const handlePopState = () => {
            const s = getInitialStep();
            setStep(s);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const [isSuccess, setIsSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Master State
    const [wizardState, setWizardState] = useState<WizardState>({
        boat: {},
        engine: {},
        service: {},
        timeAndDelivery: { timePreference: '7days', deliveryMethod: 'water' },
        contact: {}
    });

    // Helper to update state deeply
    const updateState = (section: keyof WizardState, data: any) => {
        setWizardState(prev => ({
            ...prev,
            [section]: { ...prev[section], ...data }
        }));
    };

    const handleNext = async () => {
        if (step < 5) {
            setStep((prev) => prev + 1);
            window.scrollTo(0, 0);
        } else {
            // Submit
            setIsSubmitting(true);
            setSubmitError(null);
            try {
                // Build payload from real state
                const payload = buildPayload(wizardState);
                console.log('Submitting payload:', payload);

                await submitServiceRequest(payload);
                setIsSuccess(true);
                window.scrollTo(0, 0);
            } catch (err) {
                console.error('Submission failed:', err);
                setSubmitError('Oho, jokin meni pieleen. Yritä hetken kuluttua uudelleen tai ota tarvittaessa yhteyttä info@ramstedt.fi.');
                setIsSubmitting(false);
            }
        }
    };

    const handleBack = () => {
        if (step > 1) {
            setStep((prev) => prev - 1);
            setSubmitError(null);
            window.scrollTo(0, 0);
        }
    };

    // Validation for Next Button
    const isNextDisabled = () => {
        if (isSubmitting) return true;

        switch (step) {
            case 1:
                // Require boat brand/model AND engine brand/model
                // (Strictly speaking we want at least brands, but let's be lenient for demo if needed, 
                // but requirements say "data continuity" so we should enforce at least engine for step 2)
                // Based on Step1 logic, we at least need engine ids for Step 2 to work.
                return !wizardState.engine.brand || !wizardState.engine.model;
            case 2:
                // Require a service selection (package or 'other')
                return !wizardState.service.selectedPackageId;
            case 3:
                // Always have default '7days' and 'water', so usually not disabled unless specific logic
                return false;
            case 4:
                // Require at least name and email/phone?
                // Let's require Contact Name and Phone/Email for a real feel
                return !wizardState.contact.firstName || !wizardState.contact.lastName || (!wizardState.contact.email && !wizardState.contact.phone);
            default:
                return false;
        }
    };

    if (isSuccess) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.background.offWhite,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                padding: `${spacing[48]} ${spacing[16]}`,
            }}>
                <div style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    maxWidth: '680px',
                    width: '100%',
                    padding: spacing[48],
                }}>
                    <SuccessView />
                </div>
            </div>
        );
    }

    return (
        <WizardLayout
            currentStep={step}
            steps={STEPS}
            onBack={handleBack}
            onNext={handleNext}
            isBackDisabled={step === 1 || isSubmitting}
            isNextDisabled={isNextDisabled()}
            isLastStep={step === 5}
            error={step === 5 ? submitError : null}
            isLoading={isSubmitting}
        >
            <div
                key={step}
                style={{
                    animation: 'slideInStep 200ms ease-out',
                }}
            >
                <style>{`
                    @keyframes slideInStep {
                        from {
                            opacity: 0.6;
                            transform: translateX(16px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                `}</style>
                {step === 1 && (
                    <Step1
                        // Pass existing data
                        boatData={wizardState.boat}
                        engineData={wizardState.engine}
                        // Update handler
                        onDataChange={(boat, engine) => {
                            updateState('boat', boat);
                            updateState('engine', engine);
                        }}
                    />
                )}
                {step === 2 && (
                    <Step2
                        // Pass engine IDs for Xano fetch
                        engineBrandId={parseInt(wizardState.engine.brand || '0')}
                        engineModelId={parseInt(wizardState.engine.model || '0')}
                        engineYear={parseInt(wizardState.engine.year || '0')}
                        // Pass selection
                        selectedPackageId={wizardState.service.selectedPackageId}
                        onSelect={(pkgId, details) => updateState('service', {
                            selectedPackageId: pkgId,
                            packageTitle: details?.title,
                            packagePrice: details?.price
                        })}
                    />
                )}
                {step === 3 && (
                    <Step3
                        data={wizardState.timeAndDelivery}
                        onChange={(data) => updateState('timeAndDelivery', data)}
                    />
                )}
                {step === 4 && (
                    <Step4
                        data={wizardState.contact}
                        onChange={(data) => updateState('contact', data)}
                    />
                )}
                {step === 5 && (
                    <Step5
                        data={wizardState}
                        error={submitError}
                        onNavigateToStep={(s) => { setStep(s); window.scrollTo(0, 0); }}
                    />
                )}
            </div>
        </WizardLayout>
    );
};
